import Image from 'next/image';
import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';

export interface BannerPhase {
  phase: number;
  start: string;
  countdownDate?: string | null;
  logo: 'xumoplay' | 'peacock';
  countdownTitle?: string | null;
  showLockup: boolean;
  title?: string | null;
  altTitle?: string | null;
  showBreakdown: boolean;
  disclaimer?: string | null;
  iconColor?: 'white' | 'red' | null;
  ctaText?: string | null;
  ctaButton?: string | null;
  ctaLink: string;
  altCta?: boolean;
  showTimer?: boolean;
}

const phaseData: BannerPhase[] = [
  {
    phase: 1,
    start: '2026-01-01T00:00:00.000Z',
    countdownDate: '2026-06-11T19:00:00.000Z',
    showTimer: true,
    logo: 'xumoplay',
    countdownTitle: 'Cuenta regresiva para que\u00a0empiece',
    showLockup: true,
    title: 'Copa Mundial de la FIFA\u00a02026',
    showBreakdown: true,
    disclaimer: 'Partidos inaugurales selectos en vivo y gratis en español',
    iconColor: 'white',
    ctaText: 'Prepárate\npara que\nempiece',
    ctaButton: 'Míralo gratis en Xumo Play',
    ctaLink: 'https://play.xumo.com/live-guide/telemundo-deportes-ahora?utm_source=xumocom&utm_medium=takeover&utm_campaign=fifa_pregame',
  },
  {
    phase: 2,
    start: '2026-06-11T18:35:00.000Z',
    countdownDate: '2026-06-11T19:00:00.000Z',
    showTimer: true,
    logo: 'xumoplay',
    countdownTitle: 'Míralo en vivo ahora',
    showLockup: true,
    title: 'Copa Mundial de la FIFA\u00a02026',
    showBreakdown: true,
    disclaimer: 'Partidos inaugurales selectos en vivo y gratis en español',
    iconColor: 'white',
    ctaText: 'México vs Sudáfrica',
    ctaButton: 'Míralo gratis en Xumo Play',
    ctaLink: 'https://play.xumo.com/networks/telemundo-peacock/99990626?utm_source=xumocom&utm_medium=takeover&utm_campaign=fifa_live',
  },
  {
    phase: 3,
    start: '2026-06-11T21:30:00.000Z',
    countdownDate: '2026-06-13T01:00:00.000Z',
    showTimer: true,
    logo: 'xumoplay',
    countdownTitle: 'Cuenta regresiva para que\u00a0empiece',
    showLockup: true,
    title: 'Copa Mundial de la FIFA\u00a02026',
    showBreakdown: true,
    disclaimer: 'Partidos inaugurales selectos en vivo y gratis en español',
    iconColor: 'white',
    ctaText: 'Prepárate\npara que\nempiece',
    ctaButton: 'Míralo gratis en Xumo Play',
    ctaLink: 'https://play.xumo.com/live-guide/telemundo-deportes-ahora?utm_source=xumocom&utm_medium=takeover&utm_campaign=fifa_pregame',
  },
  {
    phase: 4,
    start: '2026-06-13T00:35:00.000Z',
    countdownDate: '2026-06-13T01:00:00.000Z',
    showTimer: true,
    logo: 'xumoplay',
    countdownTitle: 'Míralo en vivo ahora',
    showLockup: true,
    title: 'Copa Mundial de la FIFA\u00a02026',
    showBreakdown: true,
    disclaimer: 'Partidos inaugurales selectos en vivo y gratis en español',
    iconColor: 'white',
    ctaText: 'Estados Unidos vs Paraguay',
    ctaButton: 'Míralo gratis en Xumo Play',
    ctaLink: 'https://play.xumo.com/networks/telemundo-peacock/99990626?utm_source=xumocom&utm_medium=takeover&utm_campaign=fifa_live',
  },
  {
    phase: 5,
    start: '2026-06-13T03:25:00.000Z',
    countdownDate: null,
    showTimer: false,
    logo: 'peacock',
    countdownTitle: 'Obtén 6 meses de Peacock Premium por sin costo extra. Aprender más.',
    showLockup: true,
    title: null,
    showBreakdown: false,
    disclaimer: null,
    altTitle: 'Disfruta toda la accion de la Copa Mundial de la FIFA 2026',
    iconColor: null,
    altCta: true,
    ctaText: null,
    ctaButton: 'Comienza en Peacock',
    ctaLink:
      'https://www.peacocktv.com/es-us/sports/copa-mundial?cid=2603sptfiwrldcpexterndedi313311&utm_campaign=2603sptfiwrldcp&utm_source=xumo_xumo_st-sv&utm_medium=ernd_edi_awa_inapnot&utm_term=xumositevisitors&utm_content=copamundial2026',
  },
];

const LOGO_SRC: Record<BannerPhase['logo'], string> = {
  xumoplay: '/static/images/refresh/2026/fifa/xumoplay.svg',
  peacock: '/static/images/refresh/2026/fifa/peacock.png',
};

function resolvePhaseWithOverride(phases: BannerPhase[]): BannerPhase | null {
  if (phases.length === 0) return null;
  const params = new URLSearchParams(window.location.search);
  const testPhase = params.get('countdown_phase');
  if (testPhase !== null) {
    const match = phases.find(p => String(p.phase) === testPhase);
    if (match) return match;
  }
  return getActivePhase(phases);
}

function getActivePhase(phases: BannerPhase[]): BannerPhase {
  const sorted = [...phases].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  const now = Date.now();
  console.log('[CountdownBanner] Current user time:', new Date(now).toISOString());
  let active = sorted[0];
  for (const p of sorted) {
    const phaseStart = new Date(p.start).getTime();
    console.log(`[CountdownBanner] Phase ${p.phase} start: ${p.start} | active: ${phaseStart <= now}`);
    if (phaseStart <= now) active = p;
  }
  console.log(`[CountdownBanner] Selected phase: ${active.phase}`);
  return active;
}

// Video
interface BackgroundVideoProps {
  hlsSrc: string;
  fallbackSrc: string;
  fallbackAlt?: string;
}

export default function BackgroundVideo({ hlsSrc, fallbackSrc, fallbackAlt = '' }: BackgroundVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mounted, setMounted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const fallback = () => setIsPlaying(false);

    video.addEventListener('playing', () => setIsPlaying(true));
    video.addEventListener('pause', fallback);
    video.addEventListener('error', fallback);

    const load = async () => {
      const { default: Hls } = await import('hls.js');

      if (Hls.isSupported()) {
        const hls = new Hls();
        hls.loadSource(hlsSrc);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(fallback);
        });
        hls.on(Hls.Events.ERROR, (_e, data) => {
          if (data.fatal) fallback();
        });
        return () => hls.destroy();
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari — native HLS
        video.src = hlsSrc;
        video.play().catch(fallback);
      } else {
        fallback();
      }
    };

    let cleanup: (() => void) | undefined;
    load().then(fn => {
      cleanup = fn;
    });
    return () => {
      cleanup?.();
      video.removeEventListener('playing', () => setIsPlaying(true));
      video.removeEventListener('pause', fallback);
      video.removeEventListener('error', fallback);
    };
  }, [hlsSrc]);

  return (
    <div className="grid h-full w-full grid-cols-1 grid-rows-1 items-center justify-items-center" aria-hidden="true">
      {/* Fallback image — fades out once video plays */}
      <motion.div
        className="pointer-events-none col-start-1 row-start-1"
        initial={{ opacity: 1 }}
        animate={{ opacity: isPlaying ? 0 : 1 }}
        transition={{ duration: 0.7, ease: 'easeInOut' }}
      >
        <Image
          src={fallbackSrc} //"/static/images/refresh/2026/fifa/FIFA_Banner_26.jpg"
          alt={fallbackAlt || 'Xumo'} //"Fifa World Cup"
          width={1920}
          height={1011}
          priority
          className="min-h-[350px] object-cover"
        />
      </motion.div>

      <video ref={videoRef} className="col-start-1 row-start-1 h-full w-full object-cover" autoPlay muted playsInline loop preload="auto" />
    </div>
  );
}

// Counter

const CountdownSection = ({ label, value }: { label: string; value: any }) => {
  return (
    <p className="flex flex-row items-center justify-center gap-1 text-3xl leading-none xs:gap-2 lg:text-4xl">
      <span className="tabular-nums">{value}</span>
      <span className="text-2xl font-light lg:text-3xl">{label}</span>
    </p>
  );
};

const Countdown = ({ date }: { date: string }) => {
  const TARGET = new Date(date);

  function getTimeLeft() {
    const diff = Math.max(0, TARGET.getTime() - Date.now());
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor(diff / 3600000) % 24,
      minutes: Math.floor(diff / 60000) % 60,
      seconds: Math.floor(diff / 1000) % 60,
    };
  }

  const [time, setTime] = useState<ReturnType<typeof getTimeLeft> | null>(null);

  useEffect(() => {
    setTime(getTimeLeft());
    const id = setInterval(() => setTime(getTimeLeft()), 1000);
    return () => clearInterval(id);
  }, []);

  const pad = (n: number) => String(n).padStart(2, '0');

  if (!time) return null;

  return (
    <div className="flex flex-row items-center justify-center gap-3 xs:gap-5">
      <CountdownSection label="D" value={pad(time.days)} />
      <CountdownSection label="H" value={pad(time.hours)} />
      <CountdownSection label="M" value={pad(time.minutes)} />
      <CountdownSection label="S" value={pad(time.seconds)} />
    </div>
  );
};
// Banner

export const CountdownBanner = ({ phases = phaseData }: { phases?: BannerPhase[] }) => {
  const [activePhase, setActivePhase] = useState<BannerPhase | null>(null);

  useEffect(() => {
    setActivePhase(resolvePhaseWithOverride(phases));
  }, [phases]);

  // Re-evaluate phase at every whole-minute boundary so transitions
  // happen automatically without a page refresh.
  // Skip when a manual ?countdown_phase override is active.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).has('countdown_phase')) return;

    let intervalId: ReturnType<typeof setInterval>;
    const msUntilNextMinute = 60000 - (Date.now() % 60000);

    const timeoutId = setTimeout(() => {
      setActivePhase(resolvePhaseWithOverride(phases));
      intervalId = setInterval(() => {
        setActivePhase(resolvePhaseWithOverride(phases));
      }, 60000);
    }, msUntilNextMinute);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [phases]);

  if (!activePhase) return null;

  const iconSrc = activePhase.iconColor ? `/static/images/refresh/2026/fifa/telemundo-${activePhase.iconColor}.png` : null;
  const ctaBorder = activePhase.altCta ? '' : 'border-[0.5px] border-white';
  const titleWidth = activePhase.showTimer ? 'lg:max-w-[280px]' : 'lg:max-w-[700px]';
  return (
    <div className="mx-auto mb-3 mt-6 flex h-full w-full max-w-[1440px] flex-col items-center justify-center text-white">
      {/* Countdown */}
      <div className="flex h-max w-full flex-row items-center justify-center gap-6 overflow-hidden border-b border-white bg-gradient-to-b from-[#190A19] to-[#64325A] px-5 py-7 xs:px-9 lg:gap-10 lg:py-4 xl:gap-20">
        <div className="flex h-max flex-col items-start justify-center gap-6 lg:flex-row lg:items-center lg:gap-10 lg:py-4 xl:gap-20">
          <Image
            src={LOGO_SRC[activePhase.logo]}
            alt={activePhase.logo === 'xumoplay' ? 'Xumo Play' : 'Peacock'}
            width={240 / 1.25}
            height={33 / 1.25}
            priority
          />
          <p className={`text-center text-lg leading-none md:text-xl ${titleWidth} lg:text-2xl`}>{activePhase.countdownTitle}</p>
          {/* counter */}
          {activePhase.showTimer && activePhase.countdownDate && <Countdown date={activePhase.countdownDate} />}
        </div>
        <div className="flex items-center justify-center lg:h-10 [@media(max-width:550px)]:hidden">
          <Image
            src="/static/images/refresh/2026/fifa/swirl-fifa.png"
            alt="Xumo Swirl"
            width={1048 / 8}
            height={723 / 8}
            priority
            className="translate-x-8 translate-y-[-40px] scale-[2.5] lg:translate-y-[-10px] lg:scale-[1.7]"
          />
        </div>
      </div>
      {/* Banner Image and Overlay */}
      {activePhase.showLockup && (
        <div className="grid h-max grid-cols-1 grid-rows-1 items-center justify-items-center overflow-hidden lg:max-h-[448px]">
          {/* Content */}
          <div className="z-20 col-start-1 row-start-1 flex h-full w-full flex-col items-start justify-end gap-3 p-8 sm:p-10">
            {/* Logo and title */}
            <Image
              src="/static/images/refresh/2026/fifa/world-cup-logos.png"
              alt="Fifa World Cup"
              width={524}
              height={225}
              priority
              className="h-auto w-[150px] pt-20 sm:w-[210px] lg:p-0"
            />
            {activePhase.title && (
              <h2 className="pb-1 text-4xl font-medium leading-[50px] xl:text-5xl">
                {activePhase.title}
                <span className="font-light">&trade;</span>
              </h2>
            )}
            {/* bottom section */}
            <div className="flex w-full flex-col items-start justify-start gap-8 lg:flex-row lg:items-end lg:justify-between lg:gap-10">
              {/* Left */}
              <div className="flex flex-col gap-3">
                {activePhase.showBreakdown && (
                  <div className="flex flex-row items-center justify-start gap-4 border-b-[0.5px] border-white pb-2 xl:gap-10 [@media(max-width:550px)]:flex-col [@media(max-width:550px)]:items-start">
                    <div className="flex flex-col">
                      <p className="mb-1 text-base font-light">Presentados en</p>
                      <Image
                        src={LOGO_SRC[activePhase.logo]}
                        alt={activePhase.logo === 'xumoplay' ? 'Xumo Play' : 'Peacock'}
                        width={240 / 1.8}
                        height={33 / 1.8}
                        priority
                      />
                    </div>

                    <div className="h-[42px] w-[0.5px] bg-white [@media(max-width:550px)]:hidden"></div>

                    <div className="flex flex-row items-center justify-center gap-4 xs:gap-6 xl:gap-10">
                      <div className="flex flex-col items-center justify-center font-bold">
                        <p className="leading-1.5 text-xl">MEX vs RSA</p>
                        <p className="text-sm">
                          Jun 11 <span className="font-light">|</span> 3 pm ET
                        </p>
                      </div>

                      <div className="h-[42px] w-[0.5px] bg-white"></div>

                      <div className="flex flex-col items-center justify-center font-bold">
                        <p className="leading-1.5 text-xl">USA vs PAR</p>
                        <p className="text-sm">
                          Jun 12 <span className="font-light">|</span> 9 pm ET
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {activePhase.disclaimer && <p className="text-[18px] font-medium leading-none sm:w-max">{activePhase.disclaimer}</p>}
                {activePhase.altTitle && (
                  <div className="flex flex-col items-start gap-3 lg:flex-row lg:items-center lg:gap-5">
                    <p className="text-2xl font-medium leading-tight sm:text-3xl lg:max-w-[380px]">
                      {activePhase.altTitle}
                      <span className="font-light">&trade;</span>
                    </p>
                    {/* mobile: horizontal rule / desktop: vertical rule */}
                    <div className="h-[1px] w-[180px] bg-white lg:h-auto lg:w-[1px] lg:shrink-0 lg:self-stretch" />
                    <Image src={LOGO_SRC[activePhase.logo]} alt={activePhase.logo === 'xumoplay' ? 'Xumo Play' : 'Peacock'} width={180} height={25} priority />
                  </div>
                )}
              </div>
              {/* Right */}
              <div className={`flex flex-row items-center justify-center gap-5 rounded-sm ${ctaBorder} p-3 [@media(max-width:550px)]:flex-wrap`}>
                {iconSrc && <Image src={iconSrc} alt="" width={55} height={51} priority />}
                {activePhase.ctaText && (
                  <p className="whitespace-pre-line text-base font-medium leading-none sm:text-lg sm:leading-[22px]">{activePhase.ctaText}</p>
                )}
                <a
                  href={activePhase.ctaLink}
                  target="_self"
                  className="w-full rounded-sm bg-white px-8 py-4 text-center text-base font-bold text-black no-underline transition-all hover:scale-105"
                >
                  {activePhase.ctaButton}
                </a>
              </div>
            </div>
          </div>
          {/* Gradient overlay */}
          <div className=" z-10 col-start-1 row-start-1 h-full w-full self-end bg-[linear-gradient(to_top,rgba(0,0,0,1)_0%,rgba(0,0,0,1)_50%,rgba(0,0,0,0)_100%)] lg:aspect-[45/14] lg:bg-[linear-gradient(to_top,rgba(0,0,0,1)_0%,rgba(0,0,0,0)_100%)]"></div>
          {/* Video/ Image */}
          <div className="col-start-1 row-start-1 self-start">
            <BackgroundVideo
              hlsSrc="https://player.vimeo.com/external/1197815485.m3u8?s=be8007a93f2c376cc8f45eb65ebc1977cd93fa67&logging=false"
              fallbackSrc="/static/images/refresh/2026/fifa/FIFA_Banner_26.jpg"
              fallbackAlt="Fifa World Cup"
            />
          </div>
        </div>
      )}
    </div>
  );
};
